/** @jsx jsx */
// =============================================================
// caly_TeamSlider.js – Team-Mitarbeiter Slider (lila Balken)
//
// Datenquelle: Contentful-Modell "teamMember"
// Slider-Technik: Swiper 6 (bereits Projekt-Abhängigkeit) –
// bewusst KEIN embla, damit package.json unverändert bleibt.
//
// Maße nach Adobe-XD "Web 1920 – 31": Band 453px hoch, lila
// #8D7BF0, Foto ~315px rund, Name/Rolle 50px mit dünner Linie,
// Zitat 18px/25. Zwei Karten sichtbar, Trennlinie dazwischen.
// Skalierung proportional zur Containerbreite (cqw, Basis 1920).
//
// Inhaltsverzeichnis:
//   1. Imports & Setup
//   2. Skalierungs-Helfer & Styled Components
//   3. Hauptkomponente caly_TeamSlider
//   4. Export
// =============================================================

// ─────────────────────────────────────────────────────────────
// 1. Imports & Setup
// ─────────────────────────────────────────────────────────────
import React from 'react'
import { jsx } from '@emotion/core'
import styled from '@emotion/styled'
import { useStaticQuery, graphql } from 'gatsby'
import Img from 'gatsby-image'
import SwiperCore, { Autoplay, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
require('swiper/swiper.scss')

SwiperCore.use([Autoplay, Navigation])

// ─────────────────────────────────────────────────────────────
// 2. Skalierungs-Helfer & Styled Components
// ─────────────────────────────────────────────────────────────

// Design-Pixel (1920er-Artboard) -> Container-Prozent mit Untergrenze
function caly_px(designPx, min) {
  const caly_cqw = (designPx / 19.2).toFixed(3)
  return `clamp(${min}px, ${caly_cqw}cqw, ${designPx}px)`
}

// Äußerer Wrapper – lila Vollbreite (Design: 1920×453, keine Rahmen)
const Caly_SliderOuter = styled.div`
  width: 100%;
  background: ${props => props.theme.colors.purple};
  position: relative;

  &:hover .caly-nav-btn {
    opacity: 1;
  }
`

// Einzelner Slide-Inhalt: Foto links, Text rechts
const Caly_Slide = styled.div`
  min-height: ${caly_px(453, 220)};
  padding: ${caly_px(60, 24)} ${caly_px(60, 16)} ${caly_px(60, 24)}
    ${caly_px(115, 20)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${caly_px(60, 16)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    /* Trennlinie zwischen zwei sichtbaren Karten */
    border-right: 1px solid #000;
  }
`

// Rundes Profilfoto (Design: ~345px, ohne Rand)
const Caly_FotoRahmen = styled.div`
  flex: 0 0 auto;
  width: ${caly_px(345, 100)};
  height: ${caly_px(345, 100)};
  border-radius: 50%;
  overflow: hidden;

  /* Gatsby-Image auf die feste Containergröße zwingen */
  .gatsby-image-wrapper {
    width: 100% !important;
    height: 100% !important;
  }
`

// Textbereich rechts vom Foto (Design: Spaltenbreite ~420px)
const Caly_Textbereich = styled.div`
  flex: 1;
  min-width: 0;
  max-width: ${caly_px(420, 230)};
`

// Name (Design: 50px SCHWARZ, dünne schwarze Linie darunter)
const Caly_Name = styled.div`
  font-size: ${caly_px(50, 20)};
  line-height: 1.3;
  color: #000;
  border-bottom: 1px solid #000;
  padding-bottom: ${caly_px(6, 3)};
  margin-bottom: ${caly_px(14, 6)};
`

// Rolle (Design: 50px WEISS, dünne schwarze Linie darunter)
const Caly_Rolle = styled.div`
  font-size: ${caly_px(50, 20)};
  line-height: 1.15;
  color: #fff;
  border-bottom: 1px solid #000;
  padding-bottom: ${caly_px(12, 5)};
  margin-bottom: ${caly_px(26, 10)};
`

// Zitat (Design: 18px/25 WEISS)
const Caly_Zitat = styled.div`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;
  color: #fff;
`

// Basis-Styles für Navigations-Buttons
const Caly_NavBtnBase = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #000;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  /* Standardmäßig unsichtbar – wird über .caly-nav-btn eingeblendet */
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #000;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.2s ease;
  }

  &:hover {
    background: #000;

    svg {
      stroke: ${props => props.theme.colors.purple};
    }
  }
`

// Vorheriger Button – links positioniert
const Caly_NavPrev = styled(Caly_NavBtnBase)`
  left: 12px;
`

// Nächster Button – rechts positioniert
const Caly_NavNext = styled(Caly_NavBtnBase)`
  right: 12px;
`

// ─────────────────────────────────────────────────────────────
// 3. Hauptkomponente caly_TeamSlider
// ─────────────────────────────────────────────────────────────

function caly_TeamSlider() {
  // Contentful-Daten: alle teamMember sortiert nach reihenfolge
  const caly_data = useStaticQuery(graphql`
    {
      allContentfulTeamMember(sort: { fields: reihenfolge, order: ASC }) {
        edges {
          node {
            contentful_id
            name
            rolle
            zitat {
              internal {
                content
              }
            }
            foto {
              fluid(
                maxWidth: 630
                maxHeight: 630
                resizingBehavior: FILL
                cropFocus: FACE
              ) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  const caly_mitglieder = caly_data.allContentfulTeamMember.edges

  return (
    <Caly_SliderOuter>
      <Swiper
        loop={true}
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        navigation={{ prevEl: '.caly-team-prev', nextEl: '.caly-team-next' }}
        slidesPerView={1}
        breakpoints={{
          1000: { slidesPerView: 2 },
        }}
      >
        {caly_mitglieder.map(function caly_renderSlide({ node }) {
          return (
            <SwiperSlide key={node.contentful_id}>
              <Caly_Slide>
                {/* Rundes Profilfoto */}
                {node.foto && (
                  <Caly_FotoRahmen>
                    <Img
                      fluid={node.foto.fluid}
                      alt={node.name}
                      imgStyle={{
                        objectFit: 'cover',
                        objectPosition: 'center top',
                      }}
                    />
                  </Caly_FotoRahmen>
                )}

                {/* Name, Rolle und Zitat */}
                <Caly_Textbereich>
                  <Caly_Name>{node.name}</Caly_Name>
                  <Caly_Rolle>{node.rolle}</Caly_Rolle>
                  {node.zitat && (
                    <Caly_Zitat>{node.zitat.internal.content}</Caly_Zitat>
                  )}
                </Caly_Textbereich>
              </Caly_Slide>
            </SwiperSlide>
          )
        })}
      </Swiper>

      {/* Navigationspfeil – zurück */}
      <Caly_NavPrev
        className="caly-nav-btn caly-team-prev"
        aria-label="Vorheriger Mitarbeiter"
      >
        <svg viewBox="0 0 24 24">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </Caly_NavPrev>

      {/* Navigationspfeil – vor */}
      <Caly_NavNext
        className="caly-nav-btn caly-team-next"
        aria-label="Nächster Mitarbeiter"
      >
        <svg viewBox="0 0 24 24">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </Caly_NavNext>
    </Caly_SliderOuter>
  )
}

// ─────────────────────────────────────────────────────────────
// 4. Export
// ─────────────────────────────────────────────────────────────

export default caly_TeamSlider
